<template>
  <v-container fluid>
    <v-alert v-if="error" type="error" dismissible @input="error=null">{{ error }}</v-alert>

    <v-dialog v-model="jsonDialog" max-width="900px">
      <v-card>
        <v-card-title>
          {{ jsonDialogTitle }}
          <v-spacer/>
          <v-btn icon @click="jsonDialog=false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <pre style="font-size:12px; overflow:auto; max-height:70vh;">{{ jsonDialogContent }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

    <coupon-bulk-apply-dialog v-model="bulkDialog" :coupon="bulkCoupon" @applied="fetchPage"/>
    <coupon-create-dialog v-model="createDialog" @created="fetchPage"/>

    <v-dialog v-model="copyDialog" max-width="600px">
      <v-card>
        <v-card-title>Copy from Stripe?</v-card-title>
        <v-card-text>
          <p>
            This page reads Fed's own copy of the coupons, because Stripe cannot search them. Copying brings that
            copy up to date.
          </p>
          <ul class="mb-3">
            <li>Lists every coupon on both Stripe accounts and every Stripe invoice Fed holds that used one</li>
            <li>Rewrites Fed's copy of each coupon, with its redemptions</li>
            <li>Marks coupons Stripe no longer has as deleted, in the copy</li>
          </ul>
          <p>
            <strong>Nothing changes in Stripe or for any customer.</strong> It runs in the background, so you can leave
            the page. A coupon created, applied or deleted here already updates the copy, so this is only needed for
            changes made in the Stripe dashboard.
          </p>
          <div v-if="copyMeta" class="caption text--secondary">
            Last copy: {{ dateFormatted(copyMeta.synced_at, {formatString: 'll HH:mm'}) }},
            {{ copyCount(copyMeta) }} coupons, took {{ Math.ceil(copyMeta.duration_ms / 60000) }} min
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="copyDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmSync">Copy now</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="600px" persistent>
      <v-card v-if="deleteItem">
        <v-card-title>Delete coupon?</v-card-title>
        <v-card-text>
          <div class="mb-3">
            <code>{{ deleteItem.id }}</code>
            <span v-if="deleteItem.name"> {{ deleteItem.name }}</span>
          </div>
          <v-progress-linear v-if="!deleteCheck && !deleteError" indeterminate/>
          <template v-if="deleteCheck">
            <v-alert v-for="(problem, i) in deleteCheck.problems" :key="`p${i}`" dense outlined type="error" class="mb-2">
              {{ problem }}
            </v-alert>
            <v-alert v-for="(note, i) in deleteCheck.notes" :key="`n${i}`" dense outlined type="warning" class="mb-2">
              {{ note }}
            </v-alert>
            <v-textarea
              v-if="deleteCheck.can_delete"
              v-model="deleteReason"
              class="mt-3"
              label="Reason (why, required)"
              rows="2"
              auto-grow
              :disabled="deleting"
            />
          </template>
          <v-alert v-if="deleteError" dense outlined type="error" class="mt-2 mb-0">{{ deleteError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text :disabled="deleting" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :disabled="!deleteCheck || !deleteCheck.can_delete || !deleteReason.trim()"
                 :loading="deleting" @click="confirmDelete">
            Delete Coupon
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card flat>
      <v-card-title class="d-flex align-center flex-wrap">
        <span>Coupons</span>
        <v-spacer/>
        <div class="caption text--secondary mr-4 text-right">
          <div v-if="syncedAt">Copied from Stripe {{ dateFormatted(syncedAt, {formatString: 'll HH:mm'}) }}</div>
          <div v-else>Never copied from Stripe</div>
          <div v-if="sync && sync.running">
            Copying {{ sync.progress.account === 'old' ? 'old' : 'new' }} account: {{ sync.progress.listed }} listed,
            {{ sync.progress.written }} written
          </div>
          <div v-else-if="sync && sync.error" class="red--text">Last copy failed: {{ sync.error }}</div>
        </div>
        <v-btn outlined :loading="sync && sync.running" :disabled="sync && sync.running" @click="openCopyDialog" class="mr-2">
          <v-icon left>mdi-cloud-download-outline</v-icon>
          Copy from Stripe
        </v-btn>
        <v-btn outlined :disabled="!total" :loading="exporting" @click="exportCsv" class="mr-2">
          <v-icon left>mdi-file-delimited-outline</v-icon>
          CSV
        </v-btn>
        <v-btn outlined color="primary" @click="openBulkApply()" class="mr-2">
          <v-icon left>mdi-account-multiple-plus-outline</v-icon>
          Add to Customers
        </v-btn>
        <v-btn color="primary" @click="createDialog = true">
          <v-icon left>mdi-plus</v-icon>
          Create Coupon
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" md="3">
            <debounced-text-field
              v-model="search"
              label="Code or name"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="kinds"
              :items="kindOptions"
              label="Kind"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="status"
              :items="statusOptions"
              label="Status"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn-toggle v-model="account" mandatory dense>
              <v-btn value="new" small>New</v-btn>
              <v-btn value="old" small>Old</v-btn>
              <v-btn value="both" small>Both</v-btn>
            </v-btn-toggle>
            <div class="caption text--secondary">Stripe account</div>
          </v-col>
          <v-col cols="12" md="2">
            <v-btn-toggle v-model="redeemed" mandatory dense>
              <v-btn value="any" small>Any</v-btn>
              <v-btn value="yes" small>Redeemed</v-btn>
              <v-btn value="no" small>Not</v-btn>
            </v-btn-toggle>
            <div class="caption text--secondary">Redemption</div>
          </v-col>
        </v-row>
        <v-row dense align="center">
          <v-col cols="12" md="3">
            <v-menu v-model="showRedeemedPicker" :close-on-content-click="false" offset-y>
              <template v-slot:activator="{on}">
                <v-text-field
                  :value="rangeText(redeemedRange)"
                  label="Redeemed between"
                  prepend-inner-icon="mdi-calendar-check"
                  readonly
                  clearable
                  dense
                  outlined
                  hide-details
                  v-on="on"
                  @click:clear="redeemedRange = []"
                />
              </template>
              <v-date-picker v-model="redeemedRange" range no-title @change="showRedeemedPicker = false"/>
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <v-menu v-model="showCreatedPicker" :close-on-content-click="false" offset-y>
              <template v-slot:activator="{on}">
                <v-text-field
                  :value="rangeText(createdRange)"
                  label="Created between"
                  prepend-inner-icon="mdi-calendar-plus"
                  readonly
                  clearable
                  dense
                  outlined
                  hide-details
                  v-on="on"
                  @click:clear="createdRange = []"
                />
              </template>
              <v-date-picker v-model="createdRange" range no-title @change="showCreatedPicker = false"/>
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <v-checkbox v-model="includeCopies" dense hide-details class="mt-0"
                        label="Include adjustment copies"/>
            <div class="caption text--secondary">
              Single-use coupons fed-lib copied so an adjusted invoice kept its discount
            </div>
          </v-col>
          <v-col cols="12" md="3" class="text-right">
            <v-btn text small @click="resetFilters">
              <v-icon left small>mdi-filter-off-outline</v-icon>
              Reset filters
            </v-btn>
          </v-col>
        </v-row>

        <div v-if="summary" class="my-3 d-flex flex-wrap align-center">
          <v-chip class="mr-2 mb-1" outlined>
            <strong>{{ summary.total }}</strong>&nbsp;coupons
          </v-chip>
          <v-chip class="mr-2 mb-1" outlined>
            <strong>{{ summary.redeemed }}</strong>&nbsp;redeemed
          </v-chip>
          <v-chip v-for="(count, kind) in summary.by_kind" :key="kind" class="mr-2 mb-1" small
                  :color="kindColor(kind)" outlined @click="toggleKind(kind)">
            {{ count }} {{ kind }}
          </v-chip>
          <v-spacer/>
          <v-chip v-if="summary.gift_card_unredeemed_value" class="mr-2 mb-1" color="teal" outlined>
            <strong>{{ formatCurrency(summary.gift_card_unredeemed_value) }}</strong>&nbsp;in unredeemed gift cards
          </v-chip>
          <v-chip v-if="summary.gift_card_redeemed_value" class="mb-1" color="grey" outlined>
            {{ formatCurrency(summary.gift_card_redeemed_value) }} in redeemed gift cards
          </v-chip>
        </div>

        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="options"
          :server-items-length="total"
          :loading="loading"
          :footer-props="{itemsPerPageOptions: [25, 50, 100, 250]}"
          :expanded.sync="expanded"
          item-key="_key"
          dense
          @click:row="toggleExpand"
        >
          <template v-slot:item.id="{item}">
            <code>{{ item.id }}</code>
            <v-chip v-if="account === 'both'" x-small outlined class="ml-1" :color="item.account === 'old' ? 'grey' : 'primary'">
              {{ item.account }}
            </v-chip>
          </template>

          <template v-slot:item.kind="{item}">
            <v-chip x-small :color="kindColor(item.kind)" dark>{{ item.kind }}</v-chip>
            <v-chip v-if="item.adjustment_copy" x-small outlined class="ml-1">copy</v-chip>
            <v-chip v-if="item.existing_customer_only" x-small outlined class="ml-1">existing only</v-chip>
          </template>

          <template v-slot:item.value="{item}">
            {{ valueText(item) }}
          </template>

          <template v-slot:item.duration="{item}">
            {{ durationText(item) }}
          </template>

          <template v-slot:item.created="{item}">
            {{ dateFormatted(item.created * 1000, {formatString: 'll'}) }}
          </template>

          <template v-slot:item.times_redeemed="{item}">
            <span :class="item.redeemed ? '' : 'text--secondary'">
              {{ item.times_redeemed }}<span v-if="item.max_redemptions != null"> / {{ item.max_redemptions }}</span>
            </span>
            <div v-if="item.redemption_count" class="caption text--secondary">
              {{ item.redemption_count }} {{ item.redemption_count === 1 ? 'invoice' : 'invoices' }}
            </div>
          </template>

          <template v-slot:item.last_redeemed_at="{item}">
            <span v-if="item.last_redeemed_at">{{ dateFormatted(item.last_redeemed_at * 1000, {formatString: 'll'}) }}</span>
            <router-link v-if="item.redeemed_by" :to="{name: 'Customer', params: {id: item.redeemed_by}}"
                         class="caption d-block" @click.native.stop>
              by {{ item.redeemed_by }}
            </router-link>
          </template>

          <template v-slot:item.status="{item}">
            <v-chip x-small outlined :color="statusColor(item)">{{ statusText(item) }}</v-chip>
            <div v-if="item.redeem_by" class="caption text--secondary">
              until {{ dateFormatted(item.redeem_by * 1000, {formatString: 'll'}) }}
            </div>
          </template>

          <template v-slot:expanded-item="{headers, item}">
            <td :colspan="headers.length" class="pl-8 pr-4 py-3 grey lighten-5">
              <div class="d-flex flex-wrap align-center mb-2">
                <v-btn outlined small :href="dashboardLink(item)" target="_blank" class="mr-2 mb-1">
                  <v-icon left small>mdi-open-in-new</v-icon>
                  Stripe Coupon
                </v-btn>
                <v-btn outlined small class="mr-2 mb-1" :loading="busy[item._key] === 'lookup'" @click.stop="lookupLive(item)">
                  <v-icon left small>mdi-cloud-search-outline</v-icon>
                  Look up live in Stripe
                </v-btn>
                <v-btn outlined small class="mr-2 mb-1" :loading="busy[item._key] === 'refresh'" @click.stop="refreshCopy(item)">
                  <v-icon left small>mdi-refresh</v-icon>
                  Refresh copy
                </v-btn>
                <v-btn outlined small class="mr-2 mb-1" @click.stop="showJson(item._detail || item, item.id)">
                  <v-icon left small>mdi-code-json</v-icon>
                  JSON
                </v-btn>
                <v-btn v-if="item.account !== 'old' && !item.deleted" outlined small color="primary" class="mr-2 mb-1"
                       @click.stop="openBulkApply(item.id)">
                  <v-icon left small>mdi-account-multiple-plus-outline</v-icon>
                  Add to customers
                </v-btn>
                <v-btn v-if="item.account !== 'old' && !item.deleted" outlined small color="error" class="mr-2 mb-1"
                       @click.stop="openDelete(item)">
                  <v-icon left small>mdi-delete-outline</v-icon>
                  Delete
                </v-btn>
                <span v-if="item.deleted" class="red--text caption ml-2">
                  No longer in Stripe (deleted{{ item.deleted_at ? ' ' + dateFormatted(item.deleted_at, {formatString: 'll'}) : '' }}).
                  A revoked coupon is deleted by fed-lib.
                </span>
              </div>

              <v-progress-linear v-if="!item._detail" indeterminate/>
              <v-row v-else dense>
                <v-col cols="12" md="4">
                  <div class="subtitle-2">Metadata</div>
                  <v-simple-table dense class="grey lighten-5" v-if="Object.keys(item._detail.metadata || {}).length">
                    <tbody>
                    <tr v-for="(value, key) in item._detail.metadata" :key="key">
                      <td class="text--secondary">{{ key }}</td>
                      <td>
                        <router-link v-if="isUidKey(key)" :to="{name: 'Customer', params: {id: value}}">{{ value }}</router-link>
                        <span v-else>{{ value }}</span>
                      </td>
                    </tr>
                    </tbody>
                  </v-simple-table>
                  <div v-else class="caption text--secondary">None</div>
                </v-col>
                <v-col cols="12" md="8">
                  <div class="subtitle-2">
                    Redemptions on invoices
                    <span class="caption text--secondary" v-if="item._detail.redemption_count > item._detail.redemptions.length">
                      (last {{ item._detail.redemptions.length }} of {{ item._detail.redemption_count }})
                    </span>
                  </div>
                  <v-simple-table dense class="grey lighten-5" v-if="item._detail.redemptions && item._detail.redemptions.length">
                    <thead>
                    <tr>
                      <th>Date</th>
                      <th>Invoice</th>
                      <th>Customer</th>
                      <th>Week</th>
                      <th class="text-right">Discount</th>
                      <th class="text-right">Total</th>
                      <th>Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="r in [...item._detail.redemptions].reverse()" :key="r.invoice">
                      <td>{{ dateFormatted(r.created * 1000, {formatString: 'll'}) }}</td>
                      <td>
                        <a :href="invoiceDashboardLink(r)" target="_blank"><code>{{ r.invoice }}</code></a>
                        <v-chip v-if="r.gift_card_redemption" x-small outlined class="ml-1">gift card credit</v-chip>
                      </td>
                      <td>
                        <router-link v-if="r.uid" :to="{name: 'Customer', params: {id: r.uid}}">{{ r.uid }}</router-link>
                        <span v-else>{{ r.customer }}</span>
                      </td>
                      <td>
                        <router-link v-if="r.week_date && r.uid"
                                     :to="{name: 'CustomerInvoiceComparison', query: {uid: r.uid, weekDate: r.week_date}}">
                          {{ r.week_date }}
                        </router-link>
                      </td>
                      <td class="text-right green--text">{{ formatCurrency(-r.discount) }}</td>
                      <td class="text-right">{{ formatCurrency(r.total) }}</td>
                      <td>{{ r.status }}</td>
                    </tr>
                    </tbody>
                  </v-simple-table>
                  <div v-else class="caption text--secondary">
                    No mirrored invoice carries this coupon<span v-if="item.times_redeemed">, though Stripe counts
                    {{ item.times_redeemed }} {{ item.times_redeemed === 1 ? 'redemption' : 'redemptions' }} (a coupon attached
                    to a customer counts as redeemed before any invoice)</span>.
                  </div>
                </v-col>
              </v-row>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import api from '@/api';
import moment from 'moment';
import CouponBulkApplyDialog from '@/components/CouponBulkApplyDialog';
import CouponCreateDialog from '@/components/CouponCreateDialog';
import DebouncedTextField from '@/components/DebouncedTextField';
import urlState from '@/router/urlState';
import {csvExport, dateFormatted, formatCurrency} from '@/store/utils';

const KINDS = ['gift card', 'named', 'referral', 'client invite', 'signup nudge', 'generated'];
const KIND_COLORS = {
  'gift card': 'teal darken-1',
  named: 'blue darken-1',
  referral: 'purple darken-1',
  'client invite': 'deep-purple darken-1',
  'signup nudge': 'orange darken-1',
  generated: 'grey darken-1',
};
const DEFAULTS = {
  search: '', kinds: [], status: 'any', account: 'new', redeemed: 'any',
  redeemedRange: [], createdRange: [], includeCopies: false,
};
const SYNC_POLL_MS = 2000;

export default {
  name: 'Coupons',
  components: {CouponBulkApplyDialog, CouponCreateDialog, DebouncedTextField},
  mixins: [urlState],

  data() {
    return {
      ...DEFAULTS,
      kindOptions: KINDS,
      statusOptions: [
        {text: 'Any status', value: 'any'},
        {text: 'Valid', value: 'valid'},
        {text: 'Expired or used up', value: 'invalid'},
        {text: 'Deleted in Stripe', value: 'deleted'},
      ],
      showRedeemedPicker: false,
      showCreatedPicker: false,
      options: {page: 1, itemsPerPage: 50, sortBy: ['created'], sortDesc: [true]},
      items: [],
      total: 0,
      summary: null,
      syncedAt: null,
      sync: null,
      syncTimer: null,
      loading: false,
      exporting: false,
      error: null,
      expanded: [],
      busy: {},
      jsonDialog: false,
      jsonDialogTitle: '',
      jsonDialogContent: '',
      bulkDialog: false,
      bulkCoupon: '',
      createDialog: false,
      copyDialog: false,
      copyMeta: null,
      deleteDialog: false,
      deleteItem: null,
      deleteCheck: null,
      deleteReason: '',
      deleteError: null,
      deleting: false,
      fetchSeq: 0,
    };
  },

  computed: {
    headers() {
      return [
        {text: 'Code', value: 'id', width: 180},
        {text: 'Name', value: 'name'},
        {text: 'Kind', value: 'kind', width: 130},
        {text: 'Value', value: 'amount_off', width: 100},
        {text: 'Duration', value: 'duration', width: 110, sortable: false},
        {text: 'Created', value: 'created', width: 120},
        {text: 'Redeemed', value: 'times_redeemed', width: 110},
        {text: 'Last redeemed', value: 'last_redeemed_at', width: 150},
        {text: 'Status', value: 'status', width: 130, sortable: false},
      ];
    },
    queryParams() {
      const [sortBy] = this.options.sortBy || [];
      const [sortDesc] = this.options.sortDesc || [];
      const sortKey = sortBy === 'amount_off' ? 'amount_off' : sortBy;
      const [redeemedFrom, redeemedTo] = this.dayBounds(this.redeemedRange);
      const [createdFrom, createdTo] = this.dayBounds(this.createdRange);
      return {
        account: this.account,
        kinds: this.kinds.join(','),
        redeemed: this.redeemed,
        status: this.status,
        search: this.search || '',
        redeemed_from: redeemedFrom,
        redeemed_to: redeemedTo,
        created_from: createdFrom,
        created_to: createdTo,
        include_copies: this.includeCopies ? 'true' : 'false',
        sort_by: sortKey || 'created',
        sort_desc: sortDesc === undefined ? 'true' : String(sortDesc),
      };
    },
  },

  watch: {
    queryParams: {
      deep: true,
      handler(now, before) {
        // a filter change starts the list again from page one
        const pagingOnly = JSON.stringify({...now, sort_by: '', sort_desc: ''}) === JSON.stringify({...before, sort_by: '', sort_desc: ''});
        if (!pagingOnly && this.options.page !== 1) {
          this.options = {...this.options, page: 1};
          return;
        }
        this.fetchPage();
      },
    },
    'options.page'() {
      this.fetchPage();
    },
    'options.itemsPerPage'() {
      this.fetchPage();
    },
  },

  mounted() {
    for (const param of ['search', 'status', 'account', 'redeemed']) {
      this.syncToUrl({param, urlParam: param, initFromRoute: true});
    }
    // lists travel as repeated keys and are left out of the URL when empty
    const listToUrl = v => (v && v.length ? v : null);
    const listFromUrl = v => (Array.isArray(v) ? v : (v ? [v] : []));
    for (const [param, urlParam] of [['kinds', 'kinds'], ['redeemedRange', 'redeemed_dates'], ['createdRange', 'created_dates']]) {
      this.syncToUrl({param, urlParam, initFromRoute: true, transformCallback: listToUrl, parseCallback: listFromUrl});
    }
    this.syncToUrl({
      param: 'includeCopies', urlParam: 'copies', initFromRoute: true,
      transformCallback: v => (v ? 'true' : null), parseCallback: v => v === true || v === 'true',
    });
    this.fetchPage();
    this.pollSync();
  },

  beforeDestroy() {
    clearTimeout(this.syncTimer);
  },

  methods: {
    dateFormatted,
    formatCurrency,

    // a date range as unix seconds covering both days whole, in the browser's zone
    dayBounds(range) {
      if (!range || !range.length) return [undefined, undefined];
      const [a, b] = [...range].sort();
      return [moment(a).startOf('day').unix(), moment(b || a).endOf('day').unix()];
    },

    rangeText(range) {
      if (!range || !range.length) return '';
      const [a, b] = [...range].sort();
      return b && b !== a ? `${a} to ${b}` : a;
    },

    resetFilters() {
      Object.assign(this, JSON.parse(JSON.stringify(DEFAULTS)));
    },

    toggleKind(kind) {
      this.kinds = this.kinds.includes(kind) ? this.kinds.filter(k => k !== kind) : [...this.kinds, kind];
    },

    async fetchPage() {
      const seq = ++this.fetchSeq;
      this.loading = true;
      this.error = null;
      try {
        const params = {
          ...this.queryParams,
          offset: (this.options.page - 1) * this.options.itemsPerPage,
          limit: this.options.itemsPerPage,
        };
        const {data} = await api.get('v2/stripe/coupons', {params});
        if (seq !== this.fetchSeq) return;   // a newer request answered already
        this.items = data.items.map(c => ({...c, _key: `${c.account}:${c.id}`, _detail: null}));
        this.total = data.total;
        this.summary = data.summary;
        this.syncedAt = data.synced_at;
        this.sync = data.sync;
        this.expanded = [];
        if (data.sync && data.sync.running) this.pollSync();
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to load coupons');
      } finally {
        if (seq === this.fetchSeq) this.loading = false;
      }
    },

    async openCopyDialog() {
      this.copyDialog = true;
      try {
        const {data} = await api.get('v2/stripe/coupons/sync');
        this.copyMeta = data.meta;
      } catch (e) {
        this.copyMeta = null;   // only the last run's figures are missing
      }
    },

    copyCount(meta) {
      return Object.values(meta.accounts || {}).reduce((sum, account) => sum + account.coupons, 0);
    },

    confirmSync() {
      this.copyDialog = false;
      this.startSync();
    },

    async openDelete(item) {
      this.deleteItem = item;
      this.deleteCheck = null;
      this.deleteReason = '';
      this.deleteError = null;
      this.deleteDialog = true;
      try {
        const {data} = await api.get(`v2/stripe/coupons/delete-check/${encodeURIComponent(item.id)}`);
        this.deleteCheck = data;
      } catch (e) {
        this.deleteError = this.messageOf(e, 'Could not check the coupon');
      }
    },

    async confirmDelete() {
      this.deleting = true;
      this.deleteError = null;
      try {
        await api.post(`v2/stripe/coupons/delete/${encodeURIComponent(this.deleteItem.id)}`, {reason: this.deleteReason.trim()});
        this.$store.commit('showSnackbar', {message: `Coupon ${this.deleteItem.id} deleted`, color: 'success'});
        this.deleteDialog = false;
        this.fetchPage();
      } catch (e) {
        this.deleteError = this.messageOf(e, 'Failed to delete the coupon');
      } finally {
        this.deleting = false;
      }
    },

    async startSync() {
      try {
        const {data} = await api.post('v2/stripe/coupons/sync');
        this.sync = data.state;
        this.pollSync();
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to start the copy');
      }
    },

    pollSync() {
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(async () => {
        try {
          const {data} = await api.get('v2/stripe/coupons/sync');
          const wasRunning = this.sync && this.sync.running;
          this.sync = data.state;
          this.syncedAt = (data.meta && data.meta.synced_at) || this.syncedAt;
          if (data.state.running) {
            this.pollSync();
          } else if (wasRunning) {
            this.$store.commit('showSnackbar', {
              message: data.state.error ? `Copy failed: ${data.state.error}` : 'Coupons copied from Stripe',
              color: data.state.error ? 'error' : 'success',
            });
            this.fetchPage();
          }
        } catch (e) {
          // the next page load shows the state again
        }
      }, SYNC_POLL_MS);
    },

    toggleExpand(item) {
      const index = this.expanded.findIndex(i => i._key === item._key);
      if (index >= 0) {
        this.expanded.splice(index, 1);
        return;
      }
      this.expanded.push(item);
      if (!item._detail) this.loadDetail(item);
    },

    async loadDetail(item) {
      try {
        const {data} = await api.get(`v2/stripe/coupons/${item.account}/${encodeURIComponent(item.id)}`);
        this.$set(item, '_detail', data);
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to load the coupon');
      }
    },

    async lookupLive(item) {
      this.$set(this.busy, item._key, 'lookup');
      try {
        const {data} = await api.get(`v2/stripe/coupons/lookup/${encodeURIComponent(item.id)}`);
        this.showJson(data, `${item.id} as Stripe has it now`);
      } catch (e) {
        this.error = this.messageOf(e, 'Lookup failed');
      } finally {
        this.$set(this.busy, item._key, null);
      }
    },

    async refreshCopy(item) {
      this.$set(this.busy, item._key, 'refresh');
      try {
        const {data} = await api.post(`v2/stripe/coupons/${item.account}/${encodeURIComponent(item.id)}/refresh`);
        Object.assign(item, data, {_detail: data});
        this.$store.commit('showSnackbar', {message: `${item.id} re-read from Stripe`, color: 'success'});
      } catch (e) {
        if (e.response && e.response.status === 404) {
          Object.assign(item, {deleted: true, deleted_at: Date.now(), valid: false});
          this.$store.commit('showSnackbar', {message: `${item.id} no longer exists in Stripe`, color: 'warning'});
        } else {
          this.error = this.messageOf(e, 'Refresh failed');
        }
      } finally {
        this.$set(this.busy, item._key, null);
      }
    },

    async exportCsv() {
      this.exporting = true;
      try {
        const rows = [];
        const limit = 500;
        for (let offset = 0; offset < this.total; offset += limit) {
          const {data} = await api.get('v2/stripe/coupons', {params: {...this.queryParams, offset, limit}});
          rows.push(...data.items);
          if (!data.items.length) break;
        }
        csvExport(rows.map(c => ({
          account: c.account,
          code: c.id,
          name: c.name || '',
          kind: c.kind,
          adjustment_copy: c.adjustment_copy ? 'yes' : '',
          existing_customer_only: c.existing_customer_only ? 'yes' : '',
          value: this.valueText(c),
          duration: this.durationText(c),
          created: dateFormatted(c.created * 1000, {formatString: 'YYYY-MM-DD'}),
          redeem_by: c.redeem_by ? dateFormatted(c.redeem_by * 1000, {formatString: 'YYYY-MM-DD'}) : '',
          times_redeemed: c.times_redeemed,
          max_redemptions: c.max_redemptions == null ? '' : c.max_redemptions,
          invoices: c.redemption_count,
          redeemed_by: c.redeemed_by || '',
          first_redeemed: c.redeemed_at ? dateFormatted(c.redeemed_at * 1000, {formatString: 'YYYY-MM-DD'}) : '',
          last_redeemed: c.last_redeemed_at ? dateFormatted(c.last_redeemed_at * 1000, {formatString: 'YYYY-MM-DD'}) : '',
          status: this.statusText(c),
        })), `coupons-${this.account}-${moment().format('YYYY-MM-DD')}.csv`);
      } catch (e) {
        this.error = this.messageOf(e, 'Export failed');
      } finally {
        this.exporting = false;
      }
    },

    valueText(c) {
      if (c.percent_off != null) return `${c.percent_off}%`;
      if (c.amount_off != null) {
        const currency = c.currency && c.currency.toLowerCase() !== 'cad' ? ` ${c.currency.toUpperCase()}` : '';
        return `${formatCurrency(c.amount_off)}${currency}`;
      }
      return '';
    },

    durationText(c) {
      if (c.duration === 'repeating') return `${c.duration_in_months} months`;
      return c.duration;
    },

    statusText(c) {
      if (c.deleted) return 'deleted';
      if (!c.valid) return c.redeem_by && c.redeem_by * 1000 < Date.now() ? 'expired' : 'used up';
      return 'valid';
    },

    statusColor(c) {
      if (c.deleted) return 'red';
      if (!c.valid) return 'grey';
      return 'green';
    },

    kindColor(kind) {
      return KIND_COLORS[kind] || 'grey';
    },

    isUidKey(key) {
      return ['redeemedBy', 'usedBy', 'referer', 'uid'].includes(key);
    },

    dashboardLink(c) {
      return `https://dashboard.stripe.com/${c.account_id}/coupons/${c.id}`;
    },

    invoiceDashboardLink(r) {
      const account = r.account === 'old' ? 'acct_1B9PlYAhKDCZ2p2o' : 'acct_1GjcI1D0KVemEdeG';
      return `https://dashboard.stripe.com/${account}/invoices/${r.invoice}`;
    },

    openBulkApply(coupon = '') {
      this.bulkCoupon = coupon;
      this.bulkDialog = true;
    },

    showJson(value, title) {
      this.jsonDialogTitle = title || '';
      this.jsonDialogContent = JSON.stringify(value, null, 2);
      this.jsonDialog = true;
    },

    messageOf(e, fallback) {
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || fallback;
    },
  },
};
</script>
